// RFQ.jsx
import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";

import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import RFQDetailsView from "./RFQDetailsView";
import MatchingOffers from "./MatchingOffers";
import TrustScoreModal from "../../../components/common/TrustScoreModal";
import { fetchData } from "../../Requirement/services/RequirementServices";
import { useLoading } from "../../../context/LoadingContext";

const RFQ = ({ filterType, userType }) => {
  const [rfqList, setRfqList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowStart, setRowStart] = useState(0);
  const [productList, setProductList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    searchProduct: "",
    searchProductGrade: "",
    searchQuantityRange: "",
  });
  const [productGradeList, setProductGradeList] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    autoClose: false,
  });

  // New state for RFQ details
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [showRFQDetails, setShowRFQDetails] = useState(false);
  const [showMatchingOffers, setShowMatchingOffers] = useState(false);

  // State for Verification Status Modal
  const [trustModal, setTrustModal] = useState(false);

  const pageSize = 5;
  const { setLoading } = useLoading();

  // Quantity options for search filter
  const quantityOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];

  // Modal handlers
  const handleModalClose = () => {
    setModal({
      show: false,
      type: "",
      title: "",
      message: "",
      autoClose: false,
    });
  };

  const trustModel = useCallback(() => {
    setTrustModal(true);
  }, []);

  // Load products for dropdowns and all products master list
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Status: "ACTIVE" },
      });

      if (response && response.items) {
        // Store all product data for grade lookup
        setAllProductMaster(response.items);

        // Create unique product list for dropdown
        const uniqueProducts = [];
        const seenProductNames = new Set();

        response.items.forEach((item) => {
          if (!seenProductNames.has(item.ProductName)) {
            seenProductNames.add(item.ProductName);
            uniqueProducts.push({
              ProductName: item.ProductName,
              ProductID: item.ProductID, // Keep first occurrence ID for reference
              ProductImagePath: item.ProductImagePath,
            });
          }
        });

        setProductList(uniqueProducts);
      } else {
        console.error(
          "Error loading products: Invalid response format",
          response
        );
        setProductList([]);
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products:", error);
      setProductList([]);
      setAllProductMaster([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load product grades based on selected product from stored data
  const loadProductGrades = useCallback(
    (productName) => {
      if (!productName || !allProductMaster.length) {
        setProductGradeList([]);
        return;
      }

      // Filter grades for the selected product from stored data
      const productGrades = allProductMaster
        .filter((item) => item.ProductName === productName)
        .map((item) => ({
          Grade: item.Grade,
          ProductID: item.ProductID,
        }))
        .filter(
          (grade, index, self) =>
            // Remove duplicate grades
            index === self.findIndex((g) => g.Grade === grade.Grade)
        );

      setProductGradeList(productGrades);
    },
    [allProductMaster]
  );

  // Search RFQs with filter support
  const searchRFQs = useCallback(
    async (searchValue = {}, newRowStart = 0) => {
      setLoading(true);
      try {
        let quantityFilter = {};
        // if (searchValue.searchQuantityRange) {
        //   if (searchValue.searchQuantityRange === "Above-500") {
        //     quantityFilter = { QuantityGTE: 500 };
        //   } else {
        //     const [min, max] = searchValue.searchQuantityRange
        //       .split("-")
        //       .map((val) => parseFloat(val));
        //     quantityFilter = { QuantityGTE: min, QuantityLTE: max };
        //   }
        // }
        const filterTerms = {};
        console.log('searchQuantityRange@@',searchValue.searchQuantityRange);
        if (searchValue.searchQuantityRange) {
          if (searchValue.searchQuantityRange === "Above-500") {
            filterTerms.QuantityGTE = 500;
          } else {
            const [min, max] = searchValue.searchQuantityRange.split("-");
            filterTerms.QuantityGTE = min;
            filterTerms.QuantityLTE = max;
          }
        }
        console.log('filterTerms@@',filterTerms);

        // Product and grade filtering
        if (searchValue.searchProduct) {
          filterTerms.ProductName = searchValue.searchProduct;
        }
        if (searchValue.requirementNumber) {
          filterTerms.RequirementNumber = searchValue.requirementNumber;
        }
        if (searchValue.searchProductGrade) {
          filterTerms.Grade = searchValue.searchProductGrade;
        }
        const today = new Date().toISOString().split("T")[0];
        let productId = "";

        // Only do product filtering if both product and grade are selected and allProductMaster is available
        if (
          searchValue.searchProduct &&
          searchValue.searchProductGrade &&
          allProductMaster.length > 0
        ) {
          const product = allProductMaster.find(
            (p) =>
              p.ProductName === searchValue.searchProduct &&
              p.Grade === searchValue.searchProductGrade
          );
          if (product) {
            productId = product.ProductID;
          }
        }

        const body = {
          target:
            userType === "Buyer" ? "listRFQByGPOffer" : "listRFQByGPOfferForGP",
          userOrder: "CreateDate DESC",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            ...filterTerms,
            custom:
              userType === "Buyer"
                ? `BuyerOfferStatus = 'Firmoffer'`
                : `GPOfferStatus = 'Firmoffer'`,
          },
        };

        // Apply userType filter
        if (userType === "Buyer") {
          body.filterTerms.custom = `BuyerOfferStatus IS NOT NULL AND (${body.filterTerms.custom})`;
        } else if (userType === "Global Partner") {
          body.filterTerms.custom = `GPOfferStatus IS NOT NULL AND (${body.filterTerms.custom})`;
        }

        if (filterType === "Request for Details") {
          const baseCustom =
            userType === "Buyer"
              ? `BuyerOfferStatus = 'Query'`
              : userType === "Global Partner"
              ? `GPOfferStatus = 'Query'`
              : `GPOfferStatus = 'Query' OR BuyerOfferStatus = 'Query'`;
          body.filterTerms.custom = baseCustom;
        }
        if (filterType === "Request for BAFO") {
          const baseCustom =
            userType === "Buyer"
              ? `BuyerOfferStatus = 'BAFO'`
              : userType === "Global Partner"
              ? `GPOfferStatus = 'BAFO'`
              : `GPOfferStatus = 'BAFO' OR BuyerOfferStatus = 'BAFO'`;
          body.filterTerms.custom = baseCustom;
        }
        if (filterType === "Accepted Offers") {
          const baseCustom =
            userType === "Buyer"
              ? `BuyerOfferStatus = 'Accepted'`
              : userType === "Global Partner"
              ? `GPOfferStatus = 'Accepted'`
              : `GPOfferStatus = 'Accepted' OR BuyerOfferStatus = 'Accepted'`;
          body.filterTerms.custom = baseCustom;
        }

        const response = await fetchData(body);
        console.log("RFQ search response:", response);

        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.RequirementsID, // Add id property with value from RequirementsID
        }));
        console.log("modifiedItems", modifiedItems);

        setRfqList(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading RFQs: ", error);
        setRfqList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, setLoading, filterType, userType, allProductMaster]
  );

  // Handle search
  const handleSearch = (searchValues) => {
    setRowStart(0);
    setCurrentPage(1);
    searchRFQs(searchValues, 0);
  };

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      console.log("Page changed to:", page);
      setCurrentPage(page);
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
      searchRFQs(searchValues, newRowStart);
    },
    [pageSize, searchRFQs, searchValues]
  );

  // Handle row click to show RFQ details and offers
  const handleRowClick = (rfq) => {
    console.log("RFQ selected:", rfq);
    setSelectedRFQ(rfq);
    setShowRFQDetails(true);
    setShowMatchingOffers(true);
  };

  // Search filter fields configuration
  const fields = [
    {
      name: "requirementNumber",
      label: "RFQ Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        loadProductGrades(value);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchQuantityRange",
      label: "Quantity Range (MT)",
      type: "dropdown",
      options: quantityOptions,
      value: searchValues.searchQuantityRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchQuantityRange: value }));
        searchRFQs({ ...searchValues, searchQuantityRange: value }, 0);
      },
    },
  ];

  // Table columns configuration
  const columns = [
    { id: "RequirementNumber", numeric: false, label: "RFQ No." },
    {
      id: "Product",
      numeric: false,
      label: "Product",
      renderCell: (row) => {
        const product = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return product ? product.ProductName : "";
      },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => {
        const product = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return product ? product.Grade : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity(MT)" },
    { id: "OriginCountry", numeric: false, label: "Origin" },
    {
      id: "DeliveryLocation",
      numeric: false,
      label: "Destination",
      renderCell: (row) => {
        let res = row.DeliveryLocation?.split("~");
        if (Array.isArray(res)) {
          return res[0];
        }
        return "";
      },
    },
    {
      id: "ShipmentPeriod",
      numeric: false,
      label: "Shipment Period",
      renderCell: (row) => {
        let dpFrom = row.DeliveryPeriodFrom
          ? dayjs(row.DeliveryPeriodFrom).format("DD-MM-YYYY")
          : "";
        let dpTill = row.DeliveryPeriodTill
          ? dayjs(row.DeliveryPeriodTill).format("DD-MM-YYYY")
          : "";
        return dpFrom !== "" && dpTill !== "" ? dpFrom + " to " + dpTill : "";
      },
    },
    {
      id: "DeliveryPort",
      numeric: false,
      label: "Delivery Port",
      renderCell: (row) => {
        let res = row.DeliveryLocation?.split("~");
        if (Array.isArray(res) && res.length > 0) {
          return res[1];
        }
        return "";
      },
    },
    { id: "Incoterm", numeric: false, label: "Incoterms" },
    {
      id: "ProductPackaging",
      numeric: false,
      label: "Packaging",
      renderCell: (row) => {
        if (!row.ProductPackaging) return "";
        try {
          const jsonLike = row.ProductPackaging.replace(/{|}/g, "")
            .split(",")
            .map((part) => part.split("="))
            .reduce((obj, [key, value]) => {
              obj[key?.trim()] = value ? value.trim() : "";
              return obj;
            }, {});
          return jsonLike.Subtype || jsonLike.item || "";
        } catch (error) {
          return "";
        }
      },
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Created",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
    {
      id: "ValidUntil",
      numeric: false,
      label: "Expires",
      renderCell: (row) =>
        row.ValidUntil ? dayjs(row.ValidUntil).format("DD-MM-YYYY") : "",
    },
    {
      id: "CreatedByEmailID",
      numeric: false,
      label: "Created By",
      renderCell: (row) => row.CreatedByEmailID || "",
    },
    {
      id: "KYBStatus",
      numeric: false,
      label: "Buyer Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.KYBStatus) {
          case "Approved":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.KYBStatus === "Approved" ? "Verified" : "Unverified"}
          </span>
        );
      },
    },
  ];

  // Effects
  // Load products on component mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Load RFQs when component mounts or filter changes
  useEffect(() => {
    console.log("RFQ filter changed:", filterType);
    // Reset to first page when filter changes
    setCurrentPage(1);
    setRowStart(0);

    // Clear selected RFQ and hide details when switching tabs
    setSelectedRFQ(null);
    setShowRFQDetails(false);
    setShowMatchingOffers(false);

    // Load RFQs immediately - no need to wait for products
    searchRFQs(searchValues, 0);
  }, [filterType, searchValues, searchRFQs]);

  return (
    <div>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">RFQs ({filterType})</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rfqList}
        total={totalItems}
        title="RFQ Information"
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        onRowClick={handleRowClick}
        selectedRows={
          selectedRFQ ? [selectedRFQ.RequirementsID || selectedRFQ.id] : []
        }
      />

      {/* RFQ Details Form */}
      {showRFQDetails && selectedRFQ && (
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">RFQ Details</h5>
                <div className="trust-score">
                  <button
                    className="submit-btn-more"
                    onClick={trustModel}
                    style={{ marginRight: "10px" }}
                  >
                    Verification Status
                  </button>
                  <button
                    className="submit-btn-more"
                    onClick={() => {
                      setShowRFQDetails(false);
                      setShowMatchingOffers(false);
                      setSelectedRFQ(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="card-body">
                <RFQDetailsView
                  rfqData={selectedRFQ}
                  allProductMaster={allProductMaster}
                  userType={userType}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matching Offers */}
      {showMatchingOffers && selectedRFQ && (
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  {filterType === "Request for Details"
                    ? `Matched Offers for RFQ #${selectedRFQ.RequirementNumber}`
                    : filterType === "Request for Firm Offers"
                    ? `Matched Offers for RFQ #${selectedRFQ.RequirementNumber}`
                    : filterType === "Request for BAFO"
                    ? `Matched Offers for RFQ #${selectedRFQ.RequirementNumber}`
                    : `Matched Offers for RFQ #${selectedRFQ.RequirementNumber}`}
                </h5>
              </div>
              <div className="card-body">
                <MatchingOffers
                  requirementDetails={selectedRFQ}
                  allProductMaster={allProductMaster}
                  filterType={filterType}
                  userType={userType}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Status Modal */}
      {trustModal && (
        <TrustScoreModal
          onClose={() => setTrustModal(false)}
          userEmail={userType === "Buyer"?selectedRFQ.BuyerEmailID:selectedRFQ.GPEmailID}
        />
      )}
    </div>
  );
};

export default RFQ;
